import { MJUICE_LOGO_URL } from '~/utils/get-filler-image';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  startTransition,
} from 'react';

interface BrandLogoContextType {
  getBrandLogo: (brandName: string) => string | null;
  isCacheInitialized: boolean;
}

const BrandLogoContext = createContext<BrandLogoContextType | undefined>(undefined);

export function BrandLogoProvider({
  children,
  initialLogos,
}: {
  children: ReactNode;
  initialLogos?: Record<string, string>;
}) {
  // Seed the cache from SSR loader data so getBrandLogo() works on FIRST render —
  // no default.webp → brand-logo flash on product cards.
  const seeded = !!initialLogos && Object.keys(initialLogos).length > 0;
  const [logoCache, setLogoCache] = useState<Record<string, string>>(initialLogos || {});
  const [isCacheInitialized, setIsCacheInitialized] = useState(seeded);

  // Helper to normalize brand names for consistent caching
  const normalizeBrandName = (name: string) => name.toUpperCase().trim();

  useEffect(() => {
    // Already seeded from SSR — skip the client fetch entirely.
    if (seeded) return;
    const fetchBrandLogos = async () => {
      try {
        console.log('🎨 [BrandLogo] Fetching brand logos from Supabase...');
        const response = await fetch('/api/brand-logos');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data && Array.isArray(data.logos)) {
          const newCache: Record<string, string> = {};
          data.logos.forEach((logo: { brand_name: string; logo_url: string }) => {
            if (logo.brand_name && logo.logo_url) {
              const normalizedName = normalizeBrandName(logo.brand_name);
              newCache[normalizedName] = logo.logo_url;
            }
          });

          // Wrap state updates in startTransition to prevent Suspense boundary updates during hydration
          // This is React's official solution for error #421
          startTransition(() => {
            setLogoCache(newCache);
            setIsCacheInitialized(true);
          });

          console.log(
            `🎨 [BrandLogo] Successfully cached ${Object.keys(newCache).length} brand logos`,
          );
          console.log(`🎨 [BrandLogo] Cached brands:`, Object.keys(newCache).sort());
        } else {
          console.warn('🎨 [BrandLogo] Invalid response format from brand logos API');
          startTransition(() => {
            setIsCacheInitialized(true);
          });
        }
      } catch (error) {
        console.error('🎨 [BrandLogo] Error fetching brand logos:', error);
        // Set to true even on error to prevent infinite loading
        startTransition(() => {
          setIsCacheInitialized(true);
        });
      }
    };

    // Use double RAF to ensure hydration is complete before fetching
    let rafId1: number;
    let rafId2: number;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        fetchBrandLogos();
      });
    });

    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
    };
  }, []); // Run once on mount

  const getBrandLogo = (brandName: string): string | null => {
    const normalizedName = normalizeBrandName(brandName);
    if (normalizedName === 'MJUICE') return MJUICE_LOGO_URL;
    return logoCache[normalizedName] || null;
  };

  const contextValue = {
    getBrandLogo,
    isCacheInitialized,
  };

  return <BrandLogoContext.Provider value={contextValue}>{children}</BrandLogoContext.Provider>;
}

export function useBrandLogoCache() {
  const context = useContext(BrandLogoContext);
  if (context === undefined) {
    throw new Error('useBrandLogoCache must be used within a BrandLogoProvider');
  }
  return context;
}
